import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket, derivePositions } from '../context/SocketContext';
import BettingControls from '../components/BettingControls';
import TileGraphic from '../components/TileGraphic';
import TableMenu from '../components/TableMenu';
import { isPoppedOut } from '../layouts/AuthLayout';
import CardGraphic from '../components/CardGraphic';
import './TableView.css';
import './table-layout.css';

// ── Chip helpers ──────────────────────────────────────────────────────────────

const CHIP_TIERS = [
  // Cardroom denominations, which a player reads without being told. The
  // previous set came from the Tailwind default palette and had five as green
  // and twenty-five as blue, so a player's instinct was actively wrong.
  { min: 1000, bg: 'var(--chip-1000)', border: 'var(--brass-dim)' },
  { min: 500,  bg: 'var(--chip-500)',  border: '#2F2439' },
  { min: 100,  bg: 'var(--chip-100)',  border: '#15171A' },
  { min: 25,   bg: 'var(--chip-25)',   border: 'var(--jade-deep)' },
  { min: 5,    bg: 'var(--chip-5)',    border: '#5A1C23' },
  { min: 1,    bg: 'var(--chip-1)',    border: 'var(--bone-shade)' },
  { min: 0,    bg: 'var(--stone-lit)', border: 'var(--rule-strong)' },
];

function chipsFromPot(pot) {
  if (pot <= 0) return [];
  const tier  = CHIP_TIERS.find(t => pot >= t.min) ?? CHIP_TIERS[CHIP_TIERS.length - 1];
  const count = Math.min(6, Math.max(1, Math.ceil(Math.log(pot + 1) / Math.log(4))));
  return Array.from({ length: count }, (_, i) => ({ ...tier, i }));
}

/* Chip flights are measured, not tabulated.
 *
 * This replaced a table of hardcoded pixel offsets from the pot to each seat,
 * which described a seat ring that no longer exists and could never have been
 * right in more than one layout anyway. Measuring the two elements works in
 * every mode — wide, short landscape, portrait, a popped-out window at any
 * size — and cannot drift when the ring moves again.
 */
function flightBetween(root, fromSel, toSel) {
  const from = root?.querySelector(fromSel);
  const to   = root?.querySelector(toSel);
  if (!from || !to) return null;

  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  const box = root.getBoundingClientRect();
  const centre = (r) => [r.left + r.width / 2, r.top + r.height / 2];
  const [ax, ay] = centre(a);
  const [bx, by] = centre(b);

  return { x: ax - box.left, y: ay - box.top, dx: bx - ax, dy: by - ay };
}

// ── Tile display ──────────────────────────────────────────────────────────────

const CARD_SUITS_SET = new Set(['Spades','Hearts','Diamonds','Clubs']);

/* One tile.
 *
 *   deal  position in a dealing sequence, which staggers the travel in. Tiles
 *         come off the stub at the middle of the table, so that is where they
 *         travel from.
 *   flip  deal face down and turn over, which is how community tiles arrive.
 *         The travel sits on the tile and the rotation on an inner element:
 *         both are transforms, and sharing one property would make the second
 *         animation cancel the first.
 */
function TileCard({ tile, faceDown, isCard, deal, flip }) {
  if (faceDown || !tile) {
    return <div className="t-tile down" style={dealStyle(deal)} data-deal={deal != null ? '' : undefined} />;
  }
  const art = isCard ? <CardGraphic tile={tile} /> : <TileGraphic tile={tile} />;

  // data-value drives the hover reveal in table-layout.css: the character
  // suit is written in kanji and the other two are counted, so the plain
  // numeral is the one thing a new player cannot get from the face.
  if (!flip) {
    return (
      <div
        className="t-tile"
        data-value={tile.value}
        data-deal={deal != null ? '' : undefined}
        style={dealStyle(deal)}
      >
        {art}
      </div>
    );
  }

  return (
    <div
      className="t-tile t-flip"
      data-value={tile.value}
      data-deal=""
      style={dealStyle(deal)}
    >
      <span className="t-flip-inner">
        <span className="t-flip-back" aria-hidden="true" />
        <span className="t-flip-face">{art}</span>
      </span>
    </div>
  );
}

const dealStyle = (deal) => (deal == null ? undefined : { '--deal-i': deal });

// Scaled-down tile for use in compact UI like the hand history replayer
function MiniTileCard({ tile, faceDown }) {
  const isCard = tile ? CARD_SUITS_SET.has(tile.suit) : false;
  return (
    <div className="hh-mini-tile-wrap">
      <TileCard tile={tile} faceDown={faceDown ?? !tile} isCard={isCard} />
    </div>
  );
}

// ── Community board ───────────────────────────────────────────────────────────

/* How much of the board every run shares: the identical leading tiles.
 *
 * Worked out from the boards themselves rather than asked of the server,
 * because it is simply where they stop matching. All in pre-flop shares
 * nothing, on the flop shares three, on the turn shares four. */
function sharedPrefix(boards) {
  if (!boards || boards.length < 2) return 0;
  const [first, ...rest] = boards;
  let n = 0;
  // Written without a callback: a closure over the loop counter is both a
  // lint error and a genuine footgun.
  while (n < first.length) {
    const tile = first[n];
    if (!tile) break;
    let same = true;
    for (const board of rest) {
      if (!board[n] || board[n].uid !== tile.uid) { same = false; break; }
    }
    if (!same) break;
    n += 1;
  }
  return n;
}

/* One row of community tiles.
 *
 * `offset` is where this row starts on the board, so the gap between the flop
 * and the turn lands in the right place whether the row is the whole board or
 * just the part that was run twice. */
function BoardRow({ tiles, offset = 0, isCard, flip = true }) {
  return (
    <div className="t-plinth">
      {tiles.map((tile, i) => {
        const abs = offset + i;
        return (
          <React.Fragment key={tile?.uid ?? `slot-${abs}`}>
            {abs === 3 && <span className="t-street-gap" />}
            <TileCard
              tile={tile} faceDown={!tile} isCard={isCard}
              deal={i} flip={flip && !!tile}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* The community board.
 *
 * When a hand was run twice, only the tiles that were actually run twice are
 * shown twice. An all-in on the flop means one flop and two turn-and-river
 * pairs, not two whole boards — repeating the shared tiles would suggest the
 * flop was dealt again, which is the opposite of what happened. */
function CommunityBoard({ tiles, boards, isCard }) {
  const runs = boards?.length > 1 ? boards : null;

  if (!runs) {
    // Mid-hand the board is short, and the places still to come show as
    // face-down slots.
    const slots = Array.from({ length: 5 }, (_, i) => tiles[i] ?? null);
    return <BoardRow tiles={slots} isCard={isCard} />;
  }

  const shared = sharedPrefix(runs);
  return (
    <div className="t-board">
      {shared > 0 && (
        <BoardRow tiles={runs[0].slice(0, shared)} isCard={isCard} />
      )}
      <div className="t-runs">
        {runs.map((board, r) => (
          <div className="t-run" key={r}>
            <span className="t-run-label">Run {r + 1}</span>
            <BoardRow tiles={board.slice(shared)} offset={shared} isCard={isCard} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Chip stack display ────────────────────────────────────────────────────────

function ChipStack({ pot }) {
  const chips = chipsFromPot(pot);
  if (!chips.length) return null;
  return (
    <div className="tv-chip-stack">
      {chips.map(c => (
        <div key={c.i} className="tv-chip" style={{ background: c.bg, borderColor: c.border }} />
      ))}
    </div>
  );
}

// ── Hand history helpers ──────────────────────────────────────────────────────

const SUIT_ABBR = { Bamboo: 'B', Characters: 'C', Circles: 'O', Spades: '♠', Hearts: '♥', Diamonds: '♦', Clubs: '♣' };
const VAL_ABBR  = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
function tileAbbr(tile) {
  if (!tile) return '?';
  return `${VAL_ABBR[tile.value] ?? tile.value}${SUIT_ABBR[tile.suit] ?? '?'}`;
}
function isRedSuit(tile) {
  return tile && ['Hearts', 'Diamonds'].includes(tile.suit);
}

const HH_STREETS     = ['preflop', 'flop', 'turn', 'river', 'showdown'];
const HH_STREET_LBLS = { preflop: 'Pre-Flop', flop: 'Flop', turn: 'Turn', river: 'River', showdown: 'Showdown' };
const HH_TILES_AT    = { preflop: 0, flop: 3, turn: 4, river: 5, showdown: 5 };

function getStreetLog(hand, street) {
  const log = hand.actionLog ?? [];
  const idx = hand.streetLogIdx ?? {};
  const start = idx[street] ?? (street === 'preflop' ? 0 : log.length);
  const next  = HH_STREETS[HH_STREETS.indexOf(street) + 1];
  const end   = next ? (idx[next] ?? log.length) : log.length;
  return log.slice(start, end);
}

// ── Hand history modal ─────────────────────────────────────────────────────────

function HandHistoryModal({ history, onClose }) {
  const [replayHand, setReplayHand] = useState(null);

  if (replayHand) {
    return <HandReplay hand={replayHand} onBack={() => setReplayHand(null)} onClose={onClose} />;
  }

  return (
    <div className="hh-backdrop" onClick={onClose}>
      <div className="hh-modal" onClick={e => e.stopPropagation()}>
        <button className="hh-close" onClick={onClose}>✕</button>
        <h2 className="hh-title">Hand History</h2>
        {(!history || history.length === 0) ? (
          <p className="hh-empty">No completed hands yet.</p>
        ) : (
          <div className="hh-list">
            {[...history].reverse().map(hand => (
              <HandSummary key={hand.handNum} hand={hand} onReplay={() => setReplayHand(hand)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HandSummary({ hand, onReplay }) {
  const hs = hand.humanSeat;
  const humanFoldedAt  = hs != null ? hand.foldedAt?.[hs] : null;
  const humanWon       = hand.winners?.some(w => w.seat === hs);
  const humanCards     = hs != null ? (hand.hands?.[hs] ?? []) : [];
  const humanHandName  = hs != null ? hand.handResults?.[hs]?.name : null;
  const humanMadeSD    = hs != null && !hand.folded?.includes(hs);

  return (
    <div className="hh-hand" onClick={onReplay} style={{ cursor: 'pointer' }}>
      <div className="hh-hand-header">
        <span className="hh-num">Hand #{hand.handNum}</span>
        <span className="hh-pot">Pot ${hand.pot.toFixed(2)}</span>
        <span className="hh-replay-hint">Replay →</span>
        <span className="hh-time">{new Date(hand.timestamp).toLocaleTimeString()}</span>
      </div>

      <div className="hh-community">
        {hand.communityTiles.slice(0, 3).map((t, i) => (
          <span key={i} className={`hh-tile ${isRedSuit(t) ? 'red' : ''}`}>{tileAbbr(t)}</span>
        ))}
        {hand.communityTiles[3] && <><span className="hh-tile-sep">·</span><span className={`hh-tile ${isRedSuit(hand.communityTiles[3]) ? 'red' : ''}`}>{tileAbbr(hand.communityTiles[3])}</span></>}
        {hand.communityTiles[4] && <><span className="hh-tile-sep">·</span><span className={`hh-tile ${isRedSuit(hand.communityTiles[4]) ? 'red' : ''}`}>{tileAbbr(hand.communityTiles[4])}</span></>}
      </div>

      {humanCards.length > 0 && (
        <div className={`hh-your-hand ${humanWon ? 'winner' : ''}`}>
          <span className="hh-your-label">You:</span>
          <div className="hh-your-cards">
            {humanCards.map((t, i) => <MiniTileCard key={i} tile={t} />)}
          </div>
          <span className={`hh-your-result ${humanWon ? 'won' : ''}`}>
            {humanWon
              ? `✓ ${humanHandName ?? 'Winner'}`
              : humanFoldedAt
                ? `Folded ${HH_STREET_LBLS[humanFoldedAt]}`
                : humanMadeSD
                  ? (humanHandName ?? 'Lost at showdown')
                  : ''}
          </span>
        </div>
      )}

      <div className="hh-winners">
        {hand.winners.map((w, i) => (
          <span key={i} className="hh-winner">
            {hand.seats[w.seat]?.player?.name} wins ${w.amount.toFixed(2)}{w.handName ? ` — ${w.handName}` : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function HandReplay({ hand, onBack, onClose }) {
  const [streetIdx, setStreetIdx] = useState(0);
  const street      = HH_STREETS[streetIdx];
  const maxTiles    = hand.communityTiles.length;
  const visibleCount = Math.min(HH_TILES_AT[street], maxTiles);

  const streetAvail = s => {
    if (s === 'preflop' || s === 'showdown') return true;
    if (s === 'flop')  return maxTiles >= 3;
    if (s === 'turn')  return maxTiles >= 4;
    if (s === 'river') return maxTiles >= 5;
    return false;
  };

  const streetPot = hand.streetPots?.[street];
  const streetBetMap = hand.streetBets?.[street] ?? {};
  const streetLog = getStreetLog(hand, street);

  const goNext = () => {
    let i = streetIdx + 1;
    while (i < HH_STREETS.length && !streetAvail(HH_STREETS[i])) i++;
    if (i < HH_STREETS.length) setStreetIdx(i);
  };
  const goPrev = () => setStreetIdx(prev => Math.max(0, prev - 1));

  const players = hand.seats.map((seat, i) => ({ seat, i })).filter(({ seat }) => seat.player);

  return (
    <div className="hh-backdrop" onClick={onClose}>
      <div className="hh-modal hh-replay-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="hh-replay-header">
          <button className="hh-back-btn" onClick={onBack}>← Back</button>
          <span className="hh-replay-title">Hand #{hand.handNum} · Pot ${hand.pot.toFixed(2)}</span>
          <button className="hh-close hh-close-inline" onClick={onClose}>✕</button>
        </div>

        {/* Street tabs */}
        <div className="hh-streets">
          {HH_STREETS.map((s, i) => (
            <button
              key={s}
              className={`hh-street-btn ${streetIdx === i ? 'active' : ''}`}
              disabled={!streetAvail(s)}
              onClick={() => setStreetIdx(i)}
            >
              {HH_STREET_LBLS[s]}
            </button>
          ))}
        </div>

        {/* Board */}
        <div className="hh-replay-board">
          <span className="hh-replay-board-label">Board</span>
          <div className="hh-replay-tiles">
            {[0,1,2,3,4].map(i => {
              const tile   = hand.communityTiles[i];
              const shown  = i < visibleCount;
              const isSep  = i === 3 || i === 4;
              return (
                <React.Fragment key={i}>
                  {isSep && <span className="hh-replay-sep">·</span>}
                  <span className={`hh-replay-tile ${shown ? `revealed${isRedSuit(tile) ? ' red' : ''}` : 'hidden'}`}>
                    {shown ? tileAbbr(tile) : '·'}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
          {(streetPot != null || street === 'showdown') && (
            <span className="hh-replay-pot">
              Pot {street === 'showdown' ? `$${hand.pot.toFixed(2)}` : `$${streetPot.toFixed(2)}`}
            </span>
          )}
        </div>

        {/* Prev / Next */}
        <div className="hh-replay-nav">
          <button className="hh-nav-btn" onClick={goPrev} disabled={streetIdx === 0}>← Prev</button>
          <span className="hh-street-current">{HH_STREET_LBLS[street]}</span>
          <button className="hh-nav-btn" onClick={goNext} disabled={streetIdx === HH_STREETS.length - 1}>Next →</button>
        </div>

        {/* Players */}
        <div className="hh-replay-players">
          {players.map(({ seat, i }) => {
            const foldedSt   = hand.foldedAt?.[i];
            const foldedIdx  = foldedSt ? HH_STREETS.indexOf(foldedSt) : -1;
            const foldedNow  = foldedIdx !== -1 && foldedIdx <= streetIdx;
            const isWinner   = hand.winners?.some(w => w.seat === i);
            const handResult = hand.handResults?.[i];
            const holeCards  = hand.hands?.[i] ?? [];
            const isHuman    = hand.humanSeat === i;
            const madeSD     = !hand.folded?.includes(i);
            const myBetAmt   = streetBetMap[i];

            return (
              <div key={i} className={`hh-rp ${foldedNow ? 'folded' : ''} ${isWinner ? 'winner' : ''} ${isHuman ? 'human' : ''}`}>
                <div className="hh-rp-info">
                  <span className="hh-rp-name">
                    {seat.player.name}
                    {isHuman && <span className="hh-rp-you"> (You)</span>}
                    {seat.player.isBot && <span className="hh-rp-bot"> bot</span>}
                  </span>
                  {foldedNow && <span className="hh-rp-badge fold">Folded {HH_STREET_LBLS[foldedSt]}</span>}
                  {street === 'showdown' && isWinner && (
                    <span className="hh-rp-badge win">
                      Won ${hand.winners.find(w => w.seat === i)?.amount.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="hh-rp-right">
                  {myBetAmt != null && myBetAmt > 0 && !foldedNow && street !== 'showdown' && (
                    <span className="hh-rp-bet">Bet ${myBetAmt.toFixed(2)}</span>
                  )}
                  <div className="hh-rp-cards">
                    {(() => {
                      const showFaceUp = isHuman || (street === 'showdown' && madeSD);
                      const showFaceDown = !isHuman && !foldedNow && holeCards.length > 0;
                      if (showFaceUp && holeCards.length > 0)
                        return holeCards.map((tile, j) => <MiniTileCard key={j} tile={tile} />);
                      if (showFaceDown)
                        return Array.from({ length: holeCards.length }, (_, j) => <MiniTileCard key={j} faceDown />);
                      return null;
                    })()}
                  </div>
                  {street === 'showdown' && handResult && madeSD && (
                    <span className="hh-rp-hand">{handResult.name}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Street action log */}
        {streetLog.length > 0 && (
          <div className="hh-replay-log">
            {streetLog.map((entry, i) => (
              <div key={i} className="hh-replay-log-entry">{entry}</div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Top-up modal ──────────────────────────────────────────────────────────────

function TopUpModal({ currentStack, maxBuyIn, wallet, onConfirm, onClose }) {
  const maxTopUp = +Math.min(wallet, maxBuyIn - currentStack).toFixed(2);
  const [amount, setAmount] = useState(Math.floor(maxTopUp));

  const handleConfirm = () => {
    const n = parseFloat(amount);
    if (!isNaN(n) && n > 0 && n <= maxTopUp) onConfirm(+n.toFixed(2));
  };

  return (
    <div className="topup-backdrop" onClick={onClose}>
      <div className="topup-modal" onClick={e => e.stopPropagation()}>
        <button className="topup-close" onClick={onClose}>✕</button>
        <h3 className="topup-title">Top Up Stack</h3>
        <div className="topup-info">
          <div className="topup-row"><span>Current stack</span><span>${currentStack.toFixed(2)}</span></div>
          <div className="topup-row"><span>Table max</span><span>${maxBuyIn.toFixed(2)}</span></div>
          <div className="topup-row"><span>Wallet balance</span><span>${wallet.toFixed(2)}</span></div>
          <div className="topup-row highlight"><span>Max top-up</span><span>${maxTopUp.toFixed(2)}</span></div>
        </div>
        {maxTopUp <= 0 ? (
          <p className="topup-at-max">{wallet <= 0 ? 'Wallet is empty.' : 'Already at max buy-in.'}</p>
        ) : (
          <>
            <input className="topup-input" type="number" min="1" max={maxTopUp} step="1"
              value={amount} onChange={e => setAmount(e.target.value)} />
            <div className="topup-actions">
              <button className="topup-btn-cancel" onClick={onClose}>Cancel</button>
              <button className="topup-btn-confirm" onClick={handleConfirm}>Add ${amount || 0}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Hand rankings modal ───────────────────────────────────────────────────────

const RANKINGS_4 = [
  { rank: 1, name: 'Pung + Pair', desc: 'Three matching tiles + a pair', freq: '~0.8%' },
  { rank: 2, name: 'Chow + Pair', desc: 'Three consecutive same-suit tiles + a pair', freq: '~8%' },
  { rank: 3, name: 'Pair only',   desc: 'Best pair from your 5 tiles', freq: '~60%' },
  { rank: 4, name: 'High Tile',   desc: 'No pair or meld — highest tile wins', freq: '~31%' },
];

function HandRankingsModal({ holeCount, deckType, onClose }) {
  const isCard = deckType === 'cards';
  const unit   = isCard ? 'cards' : 'tiles';
  const suits  = isCard ? '♠ ♥ ♦ ♣' : 'Characters · Bamboo · Circles';
  const pungEx = isCard ? 'e.g. 7♠ 7♥ 7♦' : 'e.g. Bamboo-5 × 3';
  const chowEx = isCard ? 'e.g. 4♠ 5♠ 6♠' : 'e.g. Circle-3-4-5';
  return (
    <div className="hr-backdrop" onClick={onClose}>
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <button className="hr-close" onClick={onClose}>✕</button>
        <h2 className="hr-title">Hand Rankings</h2>
        <p className="hr-subtitle">
          {holeCount === 5
            ? `Five-${isCard ? 'Card' : 'Tile'} Hi — pick exactly 2 from your 5 hole ${unit} + 3 community ${unit}`
            : `Standard — pick exactly 2 from your 4 hole ${unit} + 3 community ${unit}`}
        </p>
        <p className="hr-suits">{suits}</p>
        <div className="hr-table">
          {RANKINGS_4.map((r, i) => (
            <div key={r.rank} className="hr-row">
              <span className="hr-rank">#{i + 1}</span>
              <div className="hr-info">
                <span className="hr-name">{r.name}</span>
                <span className="hr-desc">{r.desc}</span>
              </div>
              <span className="hr-freq">{r.freq}</span>
            </div>
          ))}
        </div>
        <p className="hr-note">
          Pung = three of a kind ({pungEx}) · Chow = three consecutive same suit ({chowEx})
          {holeCount === 5 && <> · 5 hole {unit} gives 10 two-{unit} combos vs 6 in standard</>}
        </p>
      </div>
    </div>
  );
}

// ── Seat node ─────────────────────────────────────────────────────────────────

function SeatNode({ seat, index, positions, dealerSeat, game, mySession, showdown, onJoin, isSitOut, countdown, myCards, handNum }) {
  const isOccupied  = !!seat.player;
  const isActing    = game?.actionQueue?.[0] === index;
  const isFolded    = game?.folded?.includes(index);
  const isMyHand    = mySession === index;
  // During betting the hole tiles arrive on the private player:cards event,
  // never on the broadcast, so myCards is the only source for your own hand.
  const handTiles   = isMyHand ? (myCards ?? game?.hands?.[index]) : game?.hands?.[index];
  const revealHand  = isMyHand || (showdown && !isFolded);
  const isWinner    = showdown && game?.winners?.some(w => w.seat === index);
  const holeCount   = game?.config?.holeCount ?? 4;
  const isCard      = game?.config?.deckType === 'cards';
  const bet         = game?.roundBets?.[index];

  const fmt = (n) => n == null ? '—' : n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(2).replace(/\.00$/, '')}`;

  if (!isOccupied) {
    const canJoin = !!onJoin;
    return (
      <button
        type="button"
        data-seat={index}
        className={`t-seat empty t-s${index}`}
        onClick={canJoin ? () => onJoin(index) : undefined}
        disabled={!canJoin}
      >
        <span>{canJoin ? `Sit here` : `Seat ${index + 1}`}</span>
      </button>
    );
  }

  const { name, isBot } = seat.player;
  const showFan  = !isFolded;
  const fanTiles = game ? (handTiles ?? Array(holeCount).fill(null)) : Array(holeCount).fill(null);
  const stack    = game?.stacks?.[index] ?? seat.player.stack;
  // The dealer button is a physical object on the table, so it takes
  // precedence over the blind labels when a seat is both.
  const mark     = index === dealerSeat ? 'D' : positions[index];

  return (
    <div
      data-seat={index}
      className={[
        't-seat', `t-s${index}`,
        isFolded ? 'folded' : '',
        isWinner ? 'winner' : '',
        isMyHand ? 'mine' : '',
      ].filter(Boolean).join(' ')}
      /* data-live drives the lit bevel edge: the plate catches light rather
         than glowing, and rouge appears here and nowhere else. */
      data-live={isActing ? '' : undefined}
    >
      <span className="t-seat-name">
        {name}{isBot && <span className="bot-tag"> bot</span>}
      </span>

      {mark && (
        <span className={`t-seat-mark ${mark === 'D' ? 'dealer' : ''}`}>{mark}</span>
      )}

      <span className="t-seat-stack money">{fmt(stack)}</span>

      {bet > 0 && <span className="t-seat-bet money">{fmt(bet)} out</span>}
      {isSitOut && <span className="t-seat-out">sitting out</span>}

      {isActing && countdown != null && (
        <span className="t-clock">
          <span className="t-clock-fill" style={{ '--clock': `${countdown}s` }} />
        </span>
      )}

      {showFan && (
        // Keyed on the hand number so every tile remounts, and therefore
        // re-deals, at the start of each hand. The seat index offsets the
        // stagger so tiles go round the table rather than to all six seats
        // at once.
        <div className="t-seat-hand" key={handNum}>
          {fanTiles.map((t, i) => (
            <TileCard
              key={i} tile={t} faceDown={!revealHand || !t} isCard={isCard}
              deal={i * 6 + index}
            />
          ))}
        </div>
      )}

      {showdown && game?.handResults?.[index] && (
        <span className="t-seat-result">{game.handResults[index].name}</span>
      )}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

const PHASE_LABEL = { waiting: 'Waiting', preflop: 'Pre-Flop', flop: 'Flop', turn: 'Turn', river: 'River', showdown: 'Showdown' };

const VLBL = n => ({ 1: 'A', 11: 'J', 12: 'Q', 13: 'K' })[n] ?? String(n);
function formatHandDetail(result) {
  if (!result?.rank) return null;
  const { rank, meld, pair } = result;
  const pairVal = pair?.[0]?.value;
  const pairStr = pairVal != null ? `Pair of ${VLBL(pairVal)}s` : '';
  if (rank === 4) {
    const v = meld?.[0]?.value;
    return v != null ? `Pung of ${VLBL(v)}s · ${pairStr}` : pairStr;
  }
  if (rank === 3) {
    const vals = [...(meld ?? [])].sort((a, b) => a.value - b.value).map(t => VLBL(t.value));
    return `${vals.join('-')} Chow · ${pairStr}`;
  }
  if (rank === 2) return pairStr;
  if (rank === 1) {
    const v = meld ? Math.max(...meld.map(t => t.value)) : null;
    return v != null ? `High ${VLBL(v)}` : null;
  }
  return null;
}

/* The table itself, playable at whatever size its container gives it.
 *
 * Split out of the route component so a table can be rendered somewhere other
 * than the full page. It adapts to its container rather than the viewport —
 * it is a size container and its layout keys off @container — so a small
 * window gets the same layouts a phone would.
 *
 *   tableId  which table to show
 *   popped   true in its own browser window: the exit closes the window
 *            rather than returning to a lobby that is not behind it
 *   onExit   what that exit control does
 */
export function TableSurface({ tableId, popped = false, onExit }) {
  const id = tableId;
  const { user, wallet } = useAuth();
  const {
    tables, sessions, joinSeat, leaveSeat, kickBots, inviteBots, moveSeat,
    departedPlayers, DEPARTURE_WINDOW_MS,
    games, handHistory, sittingOut, waitingForBB, sitOutNextBB, runItTwice,
    autoTopUp, missedBlinds,
    beginHand, act, stopHand, togglePause, setSitOutNextBB, setRunItTwice,
    setAutoTopUp,
    queueTopUp, toggleSitOut, electStraddle, postDeadBlinds,
    myCardsMap, subscribeTable, unsubscribeTable,
  } = useSocket();

  const [showRankings, setShowRankings] = useState(false);
  const [showHistory,  setShowHistory]  = useState(false);
  const [showTopUp,    setShowTopUp]    = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [isStraddling, setIsStraddling] = useState(false);
  const [showdownCountdown, setShowdownCountdown] = useState(null);
  const [actionCountdown,   setActionCountdown]   = useState(null);

  const lastPotRef       = useRef(0);
  const tableRef         = useRef(null);
  const [flights, setFlights] = useState([]);
  const prevBets         = useRef({});

  /* Send a stack of chips from one element to another. Several can be in the
     air at once — six players calling a raise is six flights — so they are a
     list rather than a single slot, each cleaned up when it lands. */
  const sendChips = useCallback((fromSel, toSel, amount) => {
    if (!(amount > 0)) return;
    const f = flightBetween(tableRef.current, fromSel, toSel);
    if (!f) return;
    const key = `${Date.now()}-${fromSel}-${Math.random().toString(36).slice(2, 7)}`;
    setFlights(prev => [...prev, { key, amount, ...f }]);
    setTimeout(() => setFlights(prev => prev.filter(z => z.key !== key)), 700);
  }, []);

  const table     = tables.find(t => t.id === id);
  const mySession = sessions[id];  // before guard so effects can use it

  // Subscribe to this table room on mount
  useEffect(() => {
    subscribeTable(id);
    return () => unsubscribeTable(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const game  = games[id];
  const phase = game?.phase ?? 'waiting';
  const pot   = game?.pot ?? 0;

  useEffect(() => { if (pot > 0) lastPotRef.current = pot; }, [pot]);

  // Clear straddle UI state once hand starts
  useEffect(() => {
    if (phase === 'preflop' && isStraddling) setIsStraddling(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // The pot going out. One flight per winner carrying what that player won,
  // rather than the whole pot to whoever happened to be listed first — a split
  // pot, or a hand run twice with a different winner on each board, pushes to
  // each of them.
  useEffect(() => {
    if (phase !== 'showdown' || !game?.winners?.length) return;
    const timers = game.winners.map((w, i) => setTimeout(
      () => sendChips('[data-pot]', `[data-seat="${w.seat}"]`, w.amount),
      i * 120,
    ));
    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, game?.winners?.length]);

  // Money going in. Watches each seat's committed total for the street and
  // sends the difference, so a call, a raise and a blind all animate without
  // needing an event for each.
  useEffect(() => {
    const bets = game?.roundBets ?? {};
    const prev = prevBets.current;
    // A new street resets the bets; that is chips being gathered, not pushed.
    const reset = Object.keys(bets).length === 0;
    if (!reset) {
      for (const [seatKey, amount] of Object.entries(bets)) {
        const before = prev[seatKey] ?? 0;
        if (amount > before) {
          sendChips(`[data-seat="${seatKey}"]`, '[data-pot]', amount - before);
        }
      }
    }
    prevBets.current = bets;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.roundBets]);

  // Showdown countdown display
  useEffect(() => {
    if (phase !== 'showdown' || game?.stopped || game?.paused) {
      setShowdownCountdown(null);
      return;
    }
    const duration = (game?.config?.showdownDuration ?? 8);
    setShowdownCountdown(duration);
    const interval = setInterval(() => {
      setShowdownCountdown(prev => (prev != null && prev > 1 ? prev - 1 : null));
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, game?.stopped, game?.paused]);

  // Human action countdown — auto-fold + sit-out when time expires
  const isMyActionTurn = game?.actionQueue?.[0] === mySession && mySession !== undefined
    && ['preflop','flop','turn','river'].includes(phase);
  useEffect(() => {
    if (!isMyActionTurn) { setActionCountdown(null); return; }
    const limit = game?.config?.actionTimeoutSecs ?? 30;
    let rem = limit;
    setActionCountdown(rem);
    const iv = setInterval(() => {
      rem -= 1;
      setActionCountdown(rem);
      if (rem <= 0) {
        clearInterval(iv);
        setActionCountdown(null);
        if (table && mySession !== undefined) {
          act(table.id, { type: 'FOLD', seat: mySession });
          toggleSitOut(table.id, mySession, 'out');
        }
      }
    }, 1000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyActionTurn]);

  if (!table || !table.gameState) return <div className="tv-not-found">Table not found.</div>;

  // ── Derived state ───────────────────────────────────────────────────────────

  const { config }  = table;
  const sitOut      = sittingOut[id] ?? [];
  const iAmSitOut   = mySession !== undefined && sitOut.includes(mySession);
  const takenCount  = table.seats.filter(s => s.player).length;
  const isFull      = takenCount === table.max;
  const isShowdown  = phase === 'showdown';
  const isPaused    = game?.paused ?? false;

  const dealerSeat = game?.dealerSeat ?? table.gameState?.dealerSeat ?? 0;
  const straddlers = game?.straddlers ?? table.gameState?.straddlers ?? [];
  const positions  = derivePositions(table.seats, dealerSeat, straddlers);

  const fmtPot       = pot === 0 ? '—' : pot >= 1000 ? `$${(pot/1000).toFixed(2)}k` : `$${pot.toFixed(2)}`;
  const communityTiles = game?.communityTiles ?? [];

  const validActions = myCardsMap[id]?.validActions ?? null;
  const isMyTurn     = validActions?.seat === mySession && !iAmSitOut;

  const straddleLabel = !config.allowStraddles ? null
    : config.maxStraddleCount === 1 ? 'Straddle · 1×'
    : `Straddle · ${config.maxStraddleCount}×`;

  const isBettingPhase = ['preflop','flop','turn','river'].includes(phase);
  const isCard         = config.deckType === 'cards';
  const maxBuyIn       = config.maxBuyIn ?? config.bigBlind * 100;
  const myCurrentStack = game?.stacks?.[mySession] ?? table.seats[mySession]?.player?.stack ?? 0;
  const canTopUp       = mySession !== undefined && myCurrentStack < maxBuyIn && wallet > 0;
  const myHandResult     = isShowdown && mySession !== undefined ? game?.handResults?.[mySession] : null;
  const myHandName       = myHandResult?.name ?? null;
  const myHandDetail     = formatHandDetail(myHandResult);
  const tableHistory     = handHistory[id] ?? [];
  const leavingAtBB      = (sitOutNextBB[id] ?? []).includes(mySession);
  const willRunItTwice   = (runItTwice[id] ?? []).includes(mySession);
  const willAutoTopUp    = (autoTopUp[id] ?? []).includes(mySession);
  const waitingForBBList = waitingForBB[id] ?? [];
  const awaitingBB       = mySession !== undefined && waitingForBBList.includes(mySession);
  const myMissedBlinds   = missedBlinds[id]?.[mySession];
  const owesDeadBlinds   = !!(myMissedBlinds?.sb || myMissedBlinds?.bb);
  // Mirrors deadBlindAmount() in backend/engine/gameEngine.js. The server
  // splits this into live and dead money by seat position, and charges less
  // only when the big blind has reached you — in which case you are dealt in
  // rather than posting anyway.
  const deadBlindAmount  = (myMissedBlinds?.sb && myMissedBlinds?.bb)
    ? config.smallBlind + config.bigBlind
    : config.bigBlind;

  const hasBots       = table.seats.some(s => s.player?.isBot);
  const hasEmptySeats = table.seats.some(s => !s.player);
  const canInviteBots = (table.botRoster?.length ?? 0) > 0 && hasEmptySeats;
  const canSwitchSeat = mySession !== undefined && !!table.config.allowSeatChanges && ['waiting', 'showdown'].includes(phase);

  const dep = departedPlayers?.[id]?.[user?.id];
  const depWithinWindow = dep && (Date.now() - dep.departedAt < DEPARTURE_WINDOW_MS);
  const mustReturnWithStack = depWithinWindow && dep.stackAtLeave > dep.initialBuyIn;

  const handleJoinSeat = (seatIndex) => {
    if (mySession !== undefined) {
      if (!table.config.allowSeatChanges) return;
      if (['preflop', 'flop', 'turn', 'river'].includes(phase)) return;
      moveSeat(table.id, mySession, seatIndex);
    } else {
      joinSeat(table.id, seatIndex);
    }
  };

  const handleLeaveSeat = () => {
    leaveSeat(table.id);
    // wallet adjustment is handled via player:left socket event in SocketContext
  };

  const handleTopUp = (amount) => {
    queueTopUp(table.id, mySession, amount);
    // wallet adjustment handled via player:top_up_queued socket event
    setShowTopUp(false);
  };

  const handlePostBB = () => {
    postDeadBlinds(id, mySession);
  };

  const handleStraddleToggle = () => {
    if (!table || mySession === undefined) return;
    electStraddle(table.id, mySession);
    setIsStraddling(prev => !prev);
  };

  return (
    <div className={`tv-page ${popped ? 'popped' : ''}`}>

      {/* Top bar */}
      <header className="tv-topbar">
        <button className="tv-back" onClick={onExit}>
          {popped ? 'Close window' : '← Lobby'}
        </button>
        <div className="tv-topbar-info">
          <span className="tv-table-name">{table.name}</span>
          <span className={`type-badge ${{ 'No-Limit': 'nl', 'Pot-Limit': 'pl', 'Limit': 'limit', 'Spread-Limit': 'sl' }[table.type] ?? 'pl'}`}>{table.type}</span>
          {config.holeCount === 5 && <span className="tv-straddle-badge">{config.deckType === 'cards' ? '5-Card' : '5-Tile'}</span>}
          {config.deckType === 'cards' && config.holeCount !== 5 && <span className="tv-card-badge">Cards</span>}
          <span className="tv-stakes">{table.stakes}</span>
          <span className="tv-divider" />
          <span className="tv-blinds">Blinds ${config.smallBlind} / ${config.bigBlind}</span>
          {config.ante > 0 && <span className="tv-ante-badge">Ante ${config.ante}</span>}
          {config.spreadMin != null && <span className="tv-straddle-badge">Spread ${config.spreadMin}–${config.spreadMax}</span>}
          {config.raiseCap != null && <span className="tv-straddle-badge">{config.raiseCap}-raise cap</span>}
          {config.handCap != null && <span className="tv-hand-cap-badge">Cap {config.handCap >= 1000 ? `$${(config.handCap/1000).toFixed(0)}k` : `$${config.handCap}`}/hand</span>}
          {config.allowThirdBlind && <span className="tv-straddle-badge">3rd Blind</span>}
          {straddleLabel && <span className="tv-straddle-badge">{straddleLabel}</span>}
        </div>
        <div className="tv-topbar-right">
          <span className="tv-phase">{PHASE_LABEL[phase]}</span>
          <span className="tv-seated-count">{takenCount}/{table.max} seated</span>
          {/* One menu, not three. History and rankings both just open a panel,
              so they belong with the other things you occasionally ask the
              table for rather than as their own glyphs in the bar. */}
          <TableMenu
            open={menuOpen}
            onToggle={setMenuOpen}
            items={[
              {
                label: 'Hand history',
                note: tableHistory.length
                  ? `${tableHistory.length} hand${tableHistory.length === 1 ? '' : 's'}`
                  : 'none yet',
                disabled: tableHistory.length === 0,
                onSelect: () => setShowHistory(true),
              },
              {
                label: 'Hand rankings',
                onSelect: () => setShowRankings(true),
              },
              { divider: true },

              mySession !== undefined && !awaitingBB && {
                label: iAmSitOut ? 'Sit back in' : 'Sit out',
                note: iAmSitOut ? 'next hand' : 'immediately',
                onSelect: () => toggleSitOut(id, mySession, iAmSitOut ? 'in' : 'out'),
              },
              // Come back in without paying: wait for the blind to reach this
              // seat. The paid alternative is Post missed blinds below.
              mySession !== undefined && iAmSitOut && !awaitingBB && {
                label: 'Sit in next big blind',
                note: 'free',
                onSelect: () => toggleSitOut(id, mySession, 'wait'),
              },
              // Keep playing, then stop when the blind arrives — the standard
              // way to leave without paying another one.
              mySession !== undefined && !iAmSitOut && !awaitingBB && {
                label: 'Sit out next big blind',
                note: leavingAtBB ? 'on' : null,
                on: leavingAtBB,
                onSelect: () => setSitOutNextBB(id, !leavingAtBB),
              },
              // A standing preference for this seat. It only takes effect when
              // everyone still in the hand has set it.
              mySession !== undefined && {
                label: "Yes, I'll run it twice",
                note: willRunItTwice ? 'on' : 'when all in',
                on: willRunItTwice,
                onSelect: () => setRunItTwice(id, !willRunItTwice),
              },
              mySession !== undefined && owesDeadBlinds && {
                label: 'Post missed blinds',
                note: `$${deadBlindAmount % 1 === 0 ? deadBlindAmount : deadBlindAmount.toFixed(2)}`,
                tone: 'money',
                onSelect: () => postDeadBlinds(id, mySession),
              },
              mySession !== undefined && (config.allowStraddels || config.allowStraddles)
                && phase === 'waiting' && !awaitingBB && {
                label: 'Straddle next hand',
                note: isStraddling ? 'on' : null,
                on: isStraddling,
                onSelect: handleStraddleToggle,
              },
              // With this off, busting means sitting out: the player chooses
              // when to come back and with how much.
              mySession !== undefined && {
                label: 'Top up automatically',
                note: willAutoTopUp ? 'on' : 'after busting',
                on: willAutoTopUp,
                onSelect: () => setAutoTopUp(id, !willAutoTopUp),
              },
              canTopUp && {
                label: 'Top up',
                note: isBettingPhase ? 'next hand' : null,
                tone: 'money',
                onSelect: () => setShowTopUp(true),
              },
              canSwitchSeat && hasEmptySeats && {
                label: 'Move seat',
                note: 'click an empty seat',
                disabled: true,
                onSelect: () => {},
              },

              (isBettingPhase || isShowdown || hasBots || canInviteBots) && { divider: true },

              (isBettingPhase || isShowdown) && {
                label: isPaused ? 'Resume play' : 'Pause play',
                onSelect: () => togglePause(id),
              },
              isBettingPhase && {
                label: 'Stop this hand',
                onSelect: () => stopHand(id),
              },
              hasBots && !isBettingPhase && {
                label: 'Remove bots',
                onSelect: () => kickBots(id),
              },
              canInviteBots && !isBettingPhase && {
                label: 'Add bots',
                note: 'fills empty seats',
                onSelect: () => inviteBots(id),
              },

              mySession !== undefined && { divider: true },
              mySession !== undefined && {
                label: 'Leave table',
                tone: 'leave',
                onSelect: handleLeaveSeat,
              },
            ]}
          />
        </div>
      </header>

      {/* Table */}
      <main className="tv-main">
        {/* .t-table is a size container: every layout decision in
            table-layout.css keys off its dimensions, never the device's, so a
            narrowed desktop window flips to the portrait oval exactly as a
            phone does. */}
        <div className="t-table" ref={tableRef}>
          <div className="t-room" />
          <div className="t-rail" />
          <div className="t-felt" />
          <div className="t-rim" />

          {table.seats.map((seat, i) => (
            <SeatNode
              key={i}
              seat={seat}
              index={i}
              positions={positions}
              dealerSeat={dealerSeat}
              game={game}
              mySession={mySession}
              showdown={isShowdown}
              onJoin={user && (mySession === undefined || canSwitchSeat) && mySession !== i ? handleJoinSeat : undefined}
              isSitOut={sitOut.includes(i)}
              countdown={i === mySession ? actionCountdown : null}
              myCards={i === mySession ? myCardsMap[id]?.hands : undefined}
              handNum={table.handCount}
            />
          ))}

          <div className="t-centre">
            {/* The board knows whether the hand ran twice, and shows only the
                tiles that were actually run twice. The pot beneath covers
                both runs — it was split between them. */}
            <CommunityBoard
              tiles={communityTiles}
              boards={game?.boards}
              isCard={isCard}
            />
            <div className="t-pot" data-pot>
              <span className="money">{fmtPot}</span>
              <span className="t-pot-label">in the pot</span>
            </div>
            <ChipStack pot={pot} />
            {game?.straddlers?.length > 0 && (
              <div className="t-straddle">
                {game.straddlers.length === 1
                  ? 'Straddle on'
                  : `${game.straddlers.length}× straddle on`}
              </div>
            )}
          </div>

          {/* Inside the surface, not the space around it: the surface is
              centred and holds its proportions, so anchoring the cluster to
              the page would float it away from the table whenever the window
              is an extreme shape. */}
          {isMyTurn && validActions && (
            <div className="tv-betting-overlay">
              <BettingControls
                actions={validActions}
                onAction={(action) => act(id, action)}
                playerName={user?.name}
                stack={game.stacks[mySession] ?? 0}
              />
            </div>
          )}

          {flights.map(f => (
            <div
              key={f.key}
              className="t-flight"
              style={{
                left: `${f.x}px`, top: `${f.y}px`,
                '--fly-dx': `${f.dx}px`, '--fly-dy': `${f.dy}px`,
              }}
            >
              {chipsFromPot(f.amount).map((c, i) => (
                <span key={i} className="t-flight-chip"
                      style={{ background: c.bg, borderColor: c.border }} />
              ))}
            </div>
          ))}
        </div>

        {game?.log?.length > 0 && (
          <div className="tv-log">
            {[...game.log].reverse().slice(0, 8).map((entry, i) => (
              <div key={i} className={`tv-log-entry ${i === 0 ? 'latest' : ''}`}>{entry}</div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      {/* What stays out of the menu: the hand you are holding, which you read
          every hand; the winner banner, which is transient; and Start Hand,
          which is the one thing an idle table is for. Everything else moved
          into the top-bar menu — a fixed band is real estate no poker client
          spends on controls you touch a few times a session. */}
      <div className="tv-status">
        {myHandName && (
          <div className="tv-my-hand-name">
            Your hand: <strong>{myHandName}</strong>
            {myHandDetail && <span className="tv-hand-detail">{myHandDetail}</span>}
          </div>
        )}

        {phase === 'waiting' && takenCount < 2 && (
          <span className="tv-waiting-note">Waiting for 2+ players…</span>
        )}
        {phase === 'waiting' && takenCount >= 2 && (
          <button className="tv-join-btn" onClick={() => beginHand(id)}>Start hand</button>
        )}

        {isShowdown && game?.winners?.length > 0 && (
          <div className="tv-winner-banner">
            {game.winners.map((w, i) => (
              <span key={i} className="tv-winner money">
                {table.seats[w.seat]?.player?.name} wins ${w.amount.toFixed(2)}
              </span>
            ))}
            {showdownCountdown != null && (
              <span className="tv-showdown-countdown">Next hand in {showdownCountdown}s</span>
            )}
          </div>
        )}

        {awaitingBB && mySession !== undefined && (
          <div className="tv-awaiting-bb">
            <span className="tv-awaiting-bb-text">Waiting for the big blind</span>
            <button className="tv-post-bb-btn" onClick={handlePostBB}>Post it now</button>
          </div>
        )}

        {mySession === undefined && !mustReturnWithStack && (
          <span className="tv-observe-note">{isFull ? 'Table full' : 'Click an empty seat to join'}</span>
        )}
        {mySession === undefined && mustReturnWithStack && hasEmptySeats && (
          <span className="tv-return-stack-note money">
            Returning — bring ${dep.stackAtLeave.toFixed(2)}. Click a seat
          </span>
        )}
      </div>

      {showRankings && (
        <HandRankingsModal holeCount={config.holeCount ?? 4} deckType={config.deckType} onClose={() => setShowRankings(false)} />
      )}
      {showHistory && (
        <HandHistoryModal history={tableHistory} onClose={() => setShowHistory(false)} />
      )}
      {showTopUp && (
        <TopUpModal currentStack={myCurrentStack} maxBuyIn={maxBuyIn} wallet={wallet} onConfirm={handleTopUp} onClose={() => setShowTopUp(false)} />
      )}
    </div>
  );
}

/* The /table/:id route: one table, filling the viewport.
 *
 * In a popped-out window there is no lobby to go back to, so the exit closes
 * the window. Leaving the seat is a separate thing, in the table's own menu —
 * closing a window keeps your seat and chips, which is what multi-tabling
 * needs it to mean.
 */
export default function TableView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const popped = isPoppedOut();
  return (
    <TableSurface
      tableId={id}
      popped={popped}
      onExit={() => (popped ? window.close() : navigate('/dashboard'))}
    />
  );
}
